import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ordersAPI, paymentsAPI, getErrorMessage } from "../services/api";
import { useCart } from "../context/CartContext";
import { resolveImageUrl } from "../utils/image";
import toast from "react-hot-toast";

declare const Razorpay: any;

interface Address {
  full_name: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
}

const initialAddress: Address = {
  full_name: "", phone: "", address_line1: "", address_line2: "",
  city: "", state: "", pincode: "",
};

const SAVED_ADDRESS_KEY = "kushi_saved_address";

const INDIAN_STATES = [
  "Andhra Pradesh", "Assam", "Bihar", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh",
  "Uttarakhand", "West Bengal",
];

export default function CheckoutPage() {
  const [address, setAddress] = useState<Address>(initialAddress);
  const [saveAddress, setSaveAddress] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const { cart, refreshCart } = useCart();
  const navigate = useNavigate();

  // Load saved address on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SAVED_ADDRESS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Address;
        setAddress(parsed);
        setHasSaved(true);
        setSaveAddress(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const update = (field: keyof Address) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setAddress((a) => ({ ...a, [field]: e.target.value }));

  const clearSavedAddress = () => {
    localStorage.removeItem(SAVED_ADDRESS_KEY);
    setAddress(initialAddress);
    setHasSaved(false);
    setSaveAddress(false);
    toast.success("Saved address cleared");
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone
    if (!/^[6-9]\d{9}$/.test(address.phone)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    // Validate pincode
    if (!/^\d{6}$/.test(address.pincode)) {
      toast.error("Enter a valid 6-digit pincode");
      return;
    }

    setLoading(true);

    if (saveAddress) {
      localStorage.setItem(SAVED_ADDRESS_KEY, JSON.stringify(address));
    } else {
      localStorage.removeItem(SAVED_ADDRESS_KEY);
    }

    try {
      // Create order
      const orderRes = await ordersAPI.create({
        shipping_address: address,
        coupon_code: couponCode || undefined,
        notes: notes || undefined,
      });
      const order = orderRes.data;

      // Create Razorpay payment order
      const payRes = await paymentsAPI.createOrder({ order_id: order.id });
      const payData = payRes.data;

      // Load Razorpay script
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      document.body.appendChild(script);
      script.onload = () => {
        const rzp = new Razorpay({
          key: payData.key_id || "rzp_test_placeholder",
          amount: payData.amount,
          currency: "INR",
          name: "Kushi Fashions",
          description: `Order #${order.order_number}`,
          order_id: payData.razorpay_order_id,
          theme: { color: "#ec4899" },
          handler: async (response: any) => {
            try {
              await paymentsAPI.verify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              await refreshCart();
              toast.success("Payment successful! Order placed.");
              navigate(`/orders/${order.id}`);
            } catch (err) {
              toast.error("Payment verification failed. Please contact support.");
            }
          },
          modal: {
            ondismiss: () => {
              toast.error("Payment cancelled");
              setLoading(false);
            },
          },
        });
        rzp.open();
      };
    } catch (err) {
      toast.error(getErrorMessage(err));
      setLoading(false);
    }
  };

  if (!cart || cart.items.length === 0) {
    navigate("/cart");
    return null;
  }

  const shipping = cart.total >= 999 ? 0 : 79;
  const razorpayFee = Math.round(cart.total * 0.025 * 100) / 100;
  const grandTotal = cart.total + shipping + razorpayFee;

  return (
    <>
      <Helmet><title>Checkout – Kushi Fashions</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <form onSubmit={handleCheckout} className="lg:col-span-2 space-y-5">
            {/* Shipping Address */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-900">Shipping Address</h2>
                {hasSaved && (
                  <button
                    type="button"
                    onClick={clearSavedAddress}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Clear saved address
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" value={address.full_name} onChange={update("full_name")} required className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input type="tel" value={address.phone} onChange={update("phone")} required maxLength={10} placeholder="10-digit mobile number" className="input-field" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
                  <input type="text" value={address.address_line1} onChange={update("address_line1")} required className="input-field" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                  <input type="text" value={address.address_line2} onChange={update("address_line2")} className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input type="text" value={address.city} onChange={update("city")} required className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  <select value={address.state} onChange={update("state")} required className="input-field">
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                  <input type="text" value={address.pincode} onChange={update("pincode")} required maxLength={6} className="input-field" />
                </div>
              </div>

              {/* Save address toggle */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <input
                  type="checkbox"
                  id="save-address"
                  checked={saveAddress}
                  onChange={(e) => setSaveAddress(e.target.checked)}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                <label htmlFor="save-address" className="text-sm text-gray-600">
                  Save address for next time
                </label>
              </div>
            </div>

            {/* Coupon & Notes */}
            <div className="card p-5">
              <h2 className="font-bold text-gray-900 mb-4">Coupon & Notes</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="e.g. KUSHI200"
                    className="input-field uppercase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order Notes</label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special instructions?"
                    className="input-field"
                  />
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base">
              {loading ? "Processing..." : `Pay ₹${grandTotal.toLocaleString("en-IN")}`}
            </button>
            <p className="text-center text-xs text-gray-400">🔒 Secured by Razorpay</p>
          </form>

          {/* Order summary */}
          <div className="card p-5 h-fit sticky top-24">
            <h2 className="font-bold text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-3">
              {cart.items.map((item) => (
                <div key={item.id} className="flex gap-3 text-sm">
                  <img
                    src={resolveImageUrl(item.product_image, "https://via.placeholder.com/50/f9a8d4/ec4899?text=D")}
                    alt=""
                    className="w-12 h-14 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 line-clamp-1">{item.product_name}</p>
                    <p className="text-gray-400">Size: {item.size} × {item.quantity}</p>
                    <p className="text-primary-600 font-medium">₹{item.subtotal.toLocaleString("en-IN")}</p>
                  </div>
                </div>
              ))}
              <hr />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>₹{cart.total.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? "text-green-600" : ""}>
                    {shipping === 0 ? "FREE" : `₹${shipping}`}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Online payment fee (2.5%)</span>
                  <span>₹{razorpayFee.toLocaleString("en-IN")}</span>
                </div>
                <hr />
                <div className="flex justify-between font-bold text-base">
                  <span>Total</span>
                  <span>₹{grandTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
